import { makeAutoObservable, runInAction } from 'mobx'
import Papa, { type ParseResult } from 'papaparse'
import { APIDocumentType, type APIProductModel } from '@visorpro/client'
import type { Stores } from '../util/store'
import {
    type ProductModelMatching,
    type ColumnMapping,
    CREATE_NEW_MODEL,
    IGNORE_MODEL,
    DictionaryArray,
    type DocumentUpdate,
    type ProductModelUpdate,
} from '../types'
import pMap from 'p-map'
import { observed } from '../util/observed-decorator'
import _ from 'lodash'
import { visorPRORestClient } from '../util/api'
import { parseModelNames } from '../util/parse-model-names'
import { matchProductModels } from '../util/match-product-models'

export class DocumentMetadataStore {
    public csv: ParseResult<unknown> | null = null
    public columnMapping: ColumnMapping = {
        original_filename: '',
        sha512_hash: '',
        title: '',
        models: '',
        type: '',
    }
    public productModelMatchings: ProductModelMatching[] = []
    public matchMode: 'sha512_hash' | 'original_filename' = 'sha512_hash'
    public documentUpdates: DocumentUpdate[] = []
    public productManufacturerId: string | null = null
    public productCategoryId: string | null = null
    public isProcessing: boolean = false

    constructor(private readonly stores: Stores) {
        makeAutoObservable(this)
    }

    @observed('isProcessing')
    public async uploadMetadataSheet(file: File) {
        const reader = new FileReader()

        reader.readAsText(file)

        await new Promise<void>((resolve, reject) => {
            reader.onerror = () => {
                reject(new Error('Failed to read file'))
            }
            reader.onload = () => {
                resolve()
            }
        })

        const fileContent = reader.result as string
        const result = Papa.parse(fileContent, {
            header: true,
            skipEmptyLines: true,
        })

        runInAction(() => {
            this.csv = result
        })
    }

    public setColumnMapping(columnMapping: ColumnMapping) {
        runInAction(() => {
            this.columnMapping = columnMapping
        })
    }

    public setProductManufacturerId(productManufacturerId: string) {
        runInAction(() => {
            this.productManufacturerId = productManufacturerId
        })
    }

    public setProductCategoryId(productCategoryId: string) {
        runInAction(() => {
            this.productCategoryId = productCategoryId
        })
    }

    public setMatchMode(matchMode: 'sha512_hash' | 'original_filename') {
        runInAction(() => {
            this.matchMode = matchMode
        })
    }

    @observed('isProcessing')
    public async generateProductModelMatchings() {
        if (!this.csv) return

        const csvRows = this.csv.data as Record<string, string>[]
        const csvModels = csvRows.map((row) => row[this.columnMapping.models])

        const pendingModels = new Set<string>()
        for (const csvModel of csvModels) {
            const modelNames = parseModelNames(csvModel)
            for (const modelName of modelNames) {
                pendingModels.add(modelName)
            }
        }

        await this.stores.models.list()
        const productModels = Object.values(this.stores.models.modelsById)
        const manufacturerModels = productModels.filter(
            (model) =>
                model.product_manufacturer_id === this.productManufacturerId,
        )

        const modelMatchings: ProductModelMatching[] = Array.from(
            pendingModels,
        ).map((modelName) => {
            const results = matchProductModels(modelName, manufacturerModels)
            const hasPerfectMatch = results.length > 0 && results[0].score === 1

            return {
                name: modelName,
                results: results,
                selected: hasPerfectMatch ? results[0] : CREATE_NEW_MODEL,
                product_model: null,
            }
        })

        modelMatchings.sort((a, b) => {
            if (a.results.length === 0 && b.results.length === 0) return 0
            if (a.results.length === 0) return -1
            if (b.results.length === 0) return 1

            const aScore = a.results[0].score
            const bScore = b.results[0].score

            return aScore - bScore
        })

        runInAction(() => {
            this.productModelMatchings = modelMatchings
        })
    }

    public changeModelMatching(
        modelName: string,
        selected:
            | Fuzzysort.KeyResult<APIProductModel>
            | typeof CREATE_NEW_MODEL
            | typeof IGNORE_MODEL,
    ) {
        const modelMatchings = new DictionaryArray<ProductModelMatching>(
            this.productModelMatchings,
            'name',
        )
        const modelMatching = modelMatchings.get(modelName)
        if (!modelMatching) return
        modelMatching.selected = selected

        runInAction(() => {
            this.productModelMatchings = modelMatchings
        })
    }

    @observed('isProcessing')
    public async submitModelMatching() {
        if (!this.productManufacturerId || !this.productCategoryId) {
            throw new Error(
                'Product manufacturer and category IDs must be set before submitting model matching.',
            )
        }

        const modelMatchings = new DictionaryArray<ProductModelMatching>(
            this.productModelMatchings,
            'name',
        )

        await pMap(
            modelMatchings,
            async (modelMatching) => {
                modelMatching.product_model = null

                if (modelMatching.selected === CREATE_NEW_MODEL) {
                    const model = await this.stores.models.create({
                        name: modelMatching.name,
                        product_category_id: this.productCategoryId!,
                        product_manufacturer_id: this.productManufacturerId!,
                    })
                    modelMatching.product_model = model
                } else if (modelMatching.selected === IGNORE_MODEL) {
                    // Ignore the model
                } else {
                    modelMatching.product_model = modelMatching.selected.obj
                }
            },
            {
                concurrency: 10,
                stopOnError: false,
            },
        )

        runInAction(() => {
            this.productModelMatchings = modelMatchings
        })
    }

    @observed('isProcessing')
    public async generateDocumentUpdates(dataSetId: string) {
        if (!this.csv) return

        const csvRows = this.csv.data as Record<string, string>[]

        await this.stores.documents.list({
            data_set_ids: [dataSetId],
            limit: 10000,
        })
        const documents = new DictionaryArray(
            this.stores.documents.documents,
            this.matchMode,
        )
        const documentUpdates: DocumentUpdate[] = []

        for (const document of documents) {
            const csvDocuments = csvRows.filter((csvRow) => {
                const csvDocumentName =
                    csvRow[this.columnMapping.original_filename]
                const csvSha512Hash = csvRow[this.columnMapping.sha512_hash]
                const documentName = document.original_filename
                const documentSha512Hash = document.sha512_hash

                if (this.matchMode === 'sha512_hash') {
                    if (!csvSha512Hash || !documentSha512Hash) return false
                    return csvSha512Hash === documentSha512Hash || csvSha512Hash.substring(0, 32) === documentSha512Hash.substring(0, 32)
                } else if (this.matchMode === 'original_filename') {
                    if (!csvDocumentName || !documentName) return false
                    const documentNameWithoutExtension = documentName.replace(/\.pdf$/i, "")
                    const csvDocumentNameWithoutExtension = csvDocumentName.replace(/\.pdf$/i, "")

                    const csvDocumentNameCleaned = csvDocumentNameWithoutExtension
                        .replace(/[^a-zA-Z0-9]/g, '')
                        .toLowerCase()
                    const documentNameCleaned = documentNameWithoutExtension
                        .replace(/[^a-zA-Z0-9]/g, '')
                        .toLowerCase()

                    return (
                        documentNameCleaned === csvDocumentNameCleaned &&
                        csvDocumentNameCleaned !== '' &&
                        documentNameCleaned !== ''
                    )
                }
            })

            if (csvDocuments.length === 0) {
                documentUpdates.push({
                    original_filename: document.original_filename,
                    sha512_hash: document.sha512_hash,
                    old: document,
                })
            } else {
                const csvDocument = csvDocuments[0]
                const title = csvDocument[this.columnMapping.title]
                const originalFilename =
                    csvDocument[this.columnMapping.original_filename]
                const modelNames = parseModelNames(
                    csvDocument[this.columnMapping.models],
                )
                const typeName = csvDocument[this.columnMapping.type]

                const models = modelNames
                    .map((modelName) => {
                        const modelMatching = this.productModelMatchings.find(
                            (modelMatching) => modelMatching.name === modelName,
                        )
                        if (!modelMatching) return null
                        return modelMatching.product_model
                    })
                    .filter((model) => model !== null)
                const type = this.getDocumentType(typeName)

                documentUpdates.push({
                    original_filename: originalFilename,
                    sha512_hash: document.sha512_hash,
                    old: document,
                    new: {
                        ...document,
                        title,
                        document_to_product_model: models.map((model) => ({
                            product_model: model,
                        })),
                        type,
                    },
                })
            }
        }

        runInAction(() => {
            this.documentUpdates = documentUpdates
        })
    }

    private getDocumentType(typeName: string): APIDocumentType {
        typeName = typeName.toLowerCase().replace(/[^a-zA-Z0-9]/g, '')
        if (typeName.includes('service')) {
            return APIDocumentType.SERVICE_MANUAL
        } else if (typeName.includes('operator')) {
            return APIDocumentType.OPERATORS_MANUAL
        }
        return APIDocumentType.OTHER
    }

    @observed('isProcessing')
    public async updateDocuments(dataSetId: string) {
        const modelUpdates: Record<string, ProductModelUpdate> = {}

        this.documentUpdates.forEach((documentUpdate) => {
            const { old: oldDocument, new: newDocument } = documentUpdate
            if (!newDocument) return

            const documentId = oldDocument.id

            const oldModels =
                oldDocument.document_to_product_model?.map(
                    (relation) => relation.product_model.id,
                ) ?? []
            const newModels =
                newDocument.document_to_product_model?.map(
                    (relation) => relation.product_model.id,
                ) ?? []

            const deleteModels = _.difference(oldModels, newModels)
            const addModels = _.difference(newModels, oldModels)

            addModels.forEach((model) => {
                if (!modelUpdates[model]) {
                    modelUpdates[model] = {
                        model_id: model,
                        adding_document_ids: [],
                        deleting_document_ids: [],
                    }
                }
                modelUpdates[model].adding_document_ids.push(documentId)
            })

            deleteModels.forEach((model) => {
                if (!modelUpdates[model]) {
                    modelUpdates[model] = {
                        model_id: model,
                        adding_document_ids: [],
                        deleting_document_ids: [],
                    }
                }
                modelUpdates[model].deleting_document_ids.push(documentId)
            })
        })

        try {
            await pMap(
                this.documentUpdates,
                async (documentUpdate) => {
                    const { old: oldDocument, new: newDocument } =
                        documentUpdate
                    if (!newDocument) return
                    const documentId = oldDocument.id
                    await visorPRORestClient.document.update(documentId, {
                        title: newDocument.title,
                        type: newDocument.type,
                    })
                },
                {
                    concurrency: 5,
                    stopOnError: false,
                },
            )
        } catch (e) {
            console.error('Error:', e)
        }

        try {
            await pMap(
                Object.values(modelUpdates),
                async (modelUpdate) => {
                    const {
                        model_id,
                        adding_document_ids,
                        deleting_document_ids,
                    } = modelUpdate
                    const model = this.stores.models.modelsById[model_id]
                    if (!model) return

                    if (adding_document_ids.length > 0) {
                        await visorPRORestClient.model.addDocumentsToProductModel(
                            model_id,
                            adding_document_ids,
                        )
                    }

                    if (deleting_document_ids.length > 0) {
                        await visorPRORestClient.model.removeDocumentsFromProductModel(
                            model_id,
                            deleting_document_ids,
                        )
                    }
                },
                {
                    concurrency: 2,
                    stopOnError: false,
                },
            )
        } catch (e) {
            console.error('Error:', e)
        }

        await this.stores.dataSets.documentsStoresById[dataSetId].list({
            data_set_ids: [dataSetId],
            limit: 1000,
        })
    }
}
